/**
 * ProjectActivityFeed — Unified project activity timeline
 * Stitch design system
 */
import React, { useMemo, useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { draaidagPeriode } from '../../utils/nlDatum';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const TYPE_CONFIG = {
  note:        { label: 'Notitie',     icon: 'notes',       color: '#8b5cf6', filterGroup: 'communicatie' },
  meeting:     { label: 'Meeting',     icon: 'mic',         color: '#3b82f6', filterGroup: 'communicatie' },
  task:        { label: 'Taak',        icon: 'task_alt',    color: '#f59e0b', filterGroup: 'planning' },
  deliverable: { label: 'Deliverable', icon: 'movie',       color: '#ec4899', filterGroup: 'oplevering' },
  shoot_day:   { label: 'Draaidag',    icon: 'event',       color: '#10b981', filterGroup: 'planning' },
  callsheet:   { label: 'Callsheet',   icon: 'phone',       color: '#6366f1', filterGroup: 'planning' },
};

const FILTERS = [
  { key: 'alles', label: 'Alles' },
  { key: 'communicatie', label: 'Communicatie' },
  { key: 'planning', label: 'Planning' },
  { key: 'oplevering', label: 'Oplevering' },
];

// ── Normalization helpers (unchanged logic) ──────────────────────────────────

function parseDate(val) { if (!val) return null; const d = new Date(val); return isNaN(d.getTime()) ? null : d; }
function pickBestDate(...candidates) { for (const c of candidates) { const d = parseDate(c); if (d) return d; } return null; }

function normalizeNotes(notes) {
  return (notes || []).map((n, i) => {
    const f = n.fields || {};
    const date = pickBestDate(f.Aanmaakdatum, f.Aangemaakt, f.Created);
    return { id: n.id || `note-${i}`, type: 'note', title: f.Titel || f.Onderwerp || 'Notitie', description: f.Notes ? f.Notes.substring(0, 200) : '', timestamp: date, sortDate: date || new Date(0), actor: f['Created By']?.name || f.Submitter || null, status: null, action: 'toegevoegd', sourceRecord: n };
  });
}

function normalizeMeetings(meetings) {
  return (meetings || []).map((m, i) => {
    const date = pickBestDate(m.meeting_date);
    return { id: `meeting-${m.id || i}`, type: 'meeting', title: m.title || 'Meeting', description: m.summary ? m.summary.substring(0, 200) : (m.duration ? `${m.duration} minuten` : ''), timestamp: date, sortDate: date || new Date(0), actor: m.host_email || m.participants || null, status: m.status || null, action: 'gehouden', meta: { duration: m.duration, meetingId: m.id }, sourceRecord: m };
  });
}

function normalizeTasks(tasks) {
  return (tasks || []).map((t, i) => {
    const f = t.fields || {};
    const date = pickBestDate(f.Created, f.EndDateUitvoerder, f.Deadline, f.StartDateUitvoerder);
    const isCompleted = !!f.Afgerond;
    let taskName = f.NaamTaak || 'Taak';
    const dashIdx = taskName.lastIndexOf(' - ');
    if (dashIdx > 0) taskName = taskName.substring(0, dashIdx);
    return { id: t.id || `task-${i}`, type: 'task', title: taskName, description: f.Opmerking ? f.Opmerking.substring(0, 150) : '', timestamp: date, sortDate: date || new Date(0), actor: f['Voornaam Assignee']?.join?.(', ') || f['Voornaam Assignee'] || null, status: isCompleted ? 'afgerond' : (f.PrioNr || f.Type || null), action: isCompleted ? 'afgerond' : 'gepland', meta: { type: f.Type, priority: f.PrioNr, deadline: f.Deadline }, sourceRecord: t };
  });
}

function normalizeDeliverables(deliverables) {
  return (deliverables || []).map((d, i) => {
    const f = d.fields || {};
    const date = pickBestDate(f.Deadline, f.StartDatum);
    return { id: d.id || `deliv-${i}`, type: 'deliverable', title: f['Naam van Deliverable'] || f.Naam || 'Deliverable', description: [f.Formaat, f.LengteVideo ? `${f.LengteVideo}s` : null].filter(Boolean).join(' | '), timestamp: date, sortDate: date || new Date(0), actor: null, status: f.Status || null, action: f.Deadline ? 'deadline' : 'aangemaakt', meta: { format: f.Formaat, length: f.LengteVideo }, sourceRecord: d };
  });
}

function normalizeDraaidagen(draaidagen) {
  return (draaidagen || []).map((d, i) => {
    const f = d.fields || {};
    // NL-dag (12:00 lokaal) i.p.v. de UTC-tijd, die de dag ervóór gaf.
    const dag = draaidagPeriode(f).start;
    const date = pickBestDate(dag ? `${dag}T12:00:00` : null, f.DatumDraaidag);
    return { id: d.id || `draai-${i}`, type: 'shoot_day', title: f.DraaidagNaamOutlook || f.Draaidag || f.WeergaveNaamMateriaalApp || 'Draaidag', description: f.Locatie || '', timestamp: date, sortDate: date || new Date(0), actor: f['Crew namen']?.join?.(', ') || null, status: null, action: 'gepland', meta: { location: f.Locatie }, sourceRecord: d };
  });
}

function normalizeCallsheets(callsheets) {
  return (callsheets || []).map((c, i) => {
    const f = c.fields || {};
    const date = pickBestDate(f['Datum (handmatig)'], f.Datum);
    return { id: c.id || `cs-${i}`, type: 'callsheet', title: f.Naam || f.Name || 'Callsheet', description: f.Locatie || '', timestamp: date, sortDate: date || new Date(0), actor: null, status: f.Status || null, action: 'aangemaakt', sourceRecord: c };
  });
}

export function buildFeedItems({ notes, transcripts, tasks, deliverables, draaidagen, callsheets }) {
  return [
    ...normalizeNotes(notes), ...normalizeMeetings(transcripts), ...normalizeTasks(tasks),
    ...normalizeDeliverables(deliverables), ...normalizeDraaidagen(draaidagen), ...normalizeCallsheets(callsheets),
  ].sort((a, b) => b.sortDate - a.sortDate);
}

// ── Date grouping ────────────────────────────────────────────────────────────

function getDateGroup(date) {
  if (!date || date.getTime() === 0) return 'Onbekend';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const itemDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((today - itemDay) / 86400000);
  if (diffDays < 0) return 'Toekomst';
  if (diffDays === 0) return 'Vandaag';
  if (diffDays === 1) return 'Gisteren';
  if (diffDays <= 7) return 'Deze week';
  if (diffDays <= 30) return 'Deze maand';
  if (diffDays <= 90) return 'Afgelopen 3 maanden';
  return 'Eerder';
}

const GROUP_ORDER = ['Toekomst', 'Vandaag', 'Gisteren', 'Deze week', 'Deze maand', 'Afgelopen 3 maanden', 'Eerder', 'Onbekend'];

function groupByDate(items) {
  const groups = {};
  for (const item of items) { const g = getDateGroup(item.timestamp); if (!groups[g]) groups[g] = []; groups[g].push(item); }
  return GROUP_ORDER.filter(g => groups[g]?.length > 0).map(g => ({ label: g, items: groups[g] }));
}

// ── Feed item ────────────────────────────────────────────────────────────────

function FeedItem({ item, onNavigate }) {
  const [expanded, setExpanded] = useState(false);
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.note;

  const dateStr = item.timestamp && item.timestamp.getTime() !== 0
    ? item.timestamp.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: item.timestamp.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined })
    : null;
  const hasDescription = item.description && item.description.length > 0;

  return (
    <Box
      sx={{
        display: 'flex', gap: 1.5, py: 1.25, px: 1, borderRadius: '0.5rem',
        cursor: onNavigate ? 'pointer' : 'default', transition: 'background 0.15s',
        '&:hover': onNavigate ? { bgcolor: 'rgba(45,51,53,0.02)' } : {},
      }}
      onClick={() => onNavigate?.(item)}
    >
      {/* Icon dot */}
      <Box sx={{
        width: 32, height: 32, borderRadius: '0.5rem', flexShrink: 0,
        bgcolor: `${config.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={config.icon} size={16} sx={{ color: config.color }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.text, maxWidth: { xs: 200, sm: 350, md: 500 } }}>
            {item.title}
          </Typography>
          <Typography component="span" sx={{
            fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 700,
            color: config.color, bgcolor: `${config.color}12`, border: `1px solid ${config.color}25`,
            px: 0.75, py: 0.15, borderRadius: '9999px',
          }}>
            {config.label}
          </Typography>
          {item.status && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, px: 0.75, py: 0.15, borderRadius: '9999px', bgcolor: item.status === 'afgerond' ? '#dcfce7' : T.surfaceLow }}>
              {item.status === 'afgerond' && <Icon name="check_circle" size={10} sx={{ color: '#16a34a' }} />}
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.55rem', fontWeight: 600, color: item.status === 'afgerond' ? '#16a34a' : T.outline }}>
                {item.status}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Meta */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
          {item.actor && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Icon name="person" size={11} sx={{ color: T.outlineVariant }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outline }}>{item.actor}</Typography>
            </Box>
          )}
          {dateStr && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Icon name="schedule" size={11} sx={{ color: T.outlineVariant }} />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outline }}>{dateStr}</Typography>
            </Box>
          )}
        </Box>

        {/* Expandable description */}
        {hasDescription && (
          <>
            <Collapse in={expanded} collapsedSize={0}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary, mt: 0.5, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                {item.description}
              </Typography>
            </Collapse>
            <Typography
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', color: T.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {expanded ? 'Minder' : 'Meer…'}
            </Typography>
          </>
        )}
      </Box>

      {/* Navigate */}
      {onNavigate && (
        <Box onClick={(e) => { e.stopPropagation(); onNavigate(item); }} sx={{ alignSelf: 'flex-start', mt: 0.3, cursor: 'pointer', '&:hover': { opacity: 0.7 } }}>
          <Icon name="open_in_new" size={14} sx={{ color: T.outlineVariant }} />
        </Box>
      )}
    </Box>
  );
}

// ── Main feed ────────────────────────────────────────────────────────────────

export default function ProjectActivityFeed({ notes, transcripts, tasks, deliverables, draaidagen, callsheets, onNavigate }) {
  const [filter, setFilter] = useState('alles');

  const allItems = useMemo(() => buildFeedItems({ notes, transcripts, tasks, deliverables, draaidagen, callsheets }), [notes, transcripts, tasks, deliverables, draaidagen, callsheets]);
  const filteredItems = useMemo(() => filter === 'alles' ? allItems : allItems.filter(item => TYPE_CONFIG[item.type]?.filterGroup === filter), [allItems, filter]);
  const groups = useMemo(() => groupByDate(filteredItems), [filteredItems]);
  const typeCounts = useMemo(() => {
    const counts = { alles: allItems.length, communicatie: 0, planning: 0, oplevering: 0 };
    for (const item of allItems) { const g = TYPE_CONFIG[item.type]?.filterGroup; if (g) counts[g]++; }
    return counts;
  }, [allItems]);

  if (allItems.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Icon name="notes" size={48} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 12px' }} />
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 600, color: T.outline }}>Nog geen activiteit</Typography>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.outlineVariant, mt: 0.25 }}>
          Taken, meetings, notities en andere activiteit verschijnen hier zodra ze beschikbaar zijn.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filter pills */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 2.5, flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <Box
            key={f.key}
            onClick={() => setFilter(f.key)}
            sx={{
              px: 1.5, py: 0.5, borderRadius: '9999px', cursor: 'pointer',
              bgcolor: filter === f.key ? T.primaryContainer : T.surfaceLow,
              border: `1.5px solid ${filter === f.key ? `${T.primary}40` : 'transparent'}`,
              transition: 'all 0.15s',
              '&:hover': filter !== f.key ? { bgcolor: T.surfaceHigh || '#e5e9eb' } : {},
            }}
          >
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: filter === f.key ? 700 : 500, color: filter === f.key ? T.primary : T.outline }}>
              {f.label} ({typeCounts[f.key] || 0})
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Grouped feed */}
      {groups.map(group => (
        <Box key={group.label} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, px: 1 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {group.label}
            </Typography>
            <Box sx={{ flex: 1, height: 1, bgcolor: 'rgba(45,51,53,0.06)' }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.58rem', color: T.outlineVariant }}>{group.items.length}</Typography>
          </Box>
          {group.items.map(item => <FeedItem key={item.id} item={item} onNavigate={onNavigate} />)}
        </Box>
      ))}
    </Box>
  );
}
